export interface ISimpleImage {
  id: string;
  fullImageUrl: string | undefined | null;
  altText: string | null | undefined;
  vimeo_embed_id: string | null | undefined;
  caption: string | null | undefined;
}

export const EXTERNAL_IMAGE_USAGE_TAGS = {
  LIST: "list",
  THUMBNAIL: "thumb",
  FEATURED: "featured",
  LG_THUMBNAIL: "thumb-lg",
  VIDEO: "video",
};

export const GetFeaturedImage = (images: Queries.ExternalImageFragment[] | null, product_name: string): ISimpleImage | null => {
  const product_images: ISimpleImage[] = [];

  product_images.push(
    ...(images ?? [])
      .filter((ei) => ei.external_image_type.usage_tags?.includes(EXTERNAL_IMAGE_USAGE_TAGS.FEATURED))
      .sort((a, b) => (a?.external_image_type?.sort ?? 1000000) - (b?.external_image_type?.sort ?? 1000000))
      .flatMap((ei) => ({
        id: ei.id,
        fullImageUrl: `https://images.goldenartistcolors.com/${ei.full_filename}`,
        altText: `${product_name} - ${ei?.external_image_type?.name ?? ""}`,
        vimeo_embed_id: null,
        caption: ei.caption,
      })),
  );

  return product_images[0] ?? null;
};

// This function parses a productdetail fragment and returns the list images
export const GetListImagesFromProduct = (product: Queries.ProductDetailPageFragment): ISimpleImage[] => {
  const { product_name, item_parents, external_images: product_external_images, directus_files, product_videos } = product;

  const product_images: ISimpleImage[] = [];
  // First see if a featured image exist.  If so add it to be returned

  product_images.push(
    ...product_external_images
      .filter((ei) => ei.external_image_type.usage_tags?.includes(EXTERNAL_IMAGE_USAGE_TAGS.FEATURED))
      .sort((a, b) => (a?.external_image_type?.sort ?? 1000000) - (b?.external_image_type?.sort ?? 1000000))
      .flatMap((ei) => ({
        id: ei.id,
        fullImageUrl: `https://images.goldenartistcolors.com/${ei.full_filename}`,
        altText: `${product_name} - ${ei?.external_image_type?.name ?? ""}`,
        vimeo_embed_id: null,
        caption: ei.caption,
      })),
  );

  product_images.push(
    ...(directus_files ?? []).flatMap((f) => ({
      id: f?.directusFile?.id ?? "",
      fullImageUrl: f?.directusFile?.publicURL ?? "",
      altText: f?.directusFile?.name ?? "",
      vimeo_embed_id: f?.directus_files_id?.embed,
      caption: null,
    })),
  );

  // Find all the list images in the external images array and add them
  product_images.push(
    ...product_external_images
      .filter(
        (ei) =>
          ei.external_image_type.usage_tags?.includes(EXTERNAL_IMAGE_USAGE_TAGS.LIST) &&
          !ei.external_image_type.usage_tags?.includes(EXTERNAL_IMAGE_USAGE_TAGS.FEATURED),
      )
      .sort((a, b) => (a?.external_image_type?.sort ?? 1000000) - (b?.external_image_type?.sort ?? 1000000))
      .flatMap((ei) => ({
        id: ei.id,
        fullImageUrl: `https://images.goldenartistcolors.com/${ei.full_filename}`,
        altText: `${product_name} - ${ei?.external_image_type?.name ?? ""}`,
        vimeo_embed_id: null,
        caption: ei.caption,
      })),
  );

  // Now add all the videos from the product videos array.  These use vimeo urls directly
  const safe_videos = (product_videos ?? []).flatMap((pv) => (pv && pv.vimeo_video_url ? [pv.vimeo_video_url] : []));

  product_images.push(
    ...safe_videos.map((url, i) => ({
      id: `video-${i}`,
      fullImageUrl: null,
      altText: `${product_name} `,
      vimeo_embed_id: url.slice(18),
      caption: null,
    })),
  );

  // Finally, add all the list images from the individual items.  This should include the packaging images
  const files: ISimpleImage[] = item_parents.flatMap((ip) =>
    ip.items.flatMap((i) =>
      i.external_images
        .filter((ei) => ei.external_image_type.usage_tags?.includes(EXTERNAL_IMAGE_USAGE_TAGS.LIST))
        .sort((a, b) => (a?.external_image_type?.sort ?? 1000000) - (b?.external_image_type?.sort ?? 1000000))
        .flatMap((ei) => ({
          id: ei.id,
          fullImageUrl: `https://images.goldenartistcolors.com/${ei.full_filename}`,
          altText: `${product_name} - ${i.size.description} - ${ei?.external_image_type?.name ?? ""}`,
          vimeo_embed_id: null,
          caption: ei.caption,
        })),
    ),
  );

  product_images.push(...files);

  product_images.push(
    ...product_external_images
      .filter(
        (ei) =>
          ei.external_image_type.usage_tags?.includes(EXTERNAL_IMAGE_USAGE_TAGS.VIDEO) &&
          !ei.external_image_type.usage_tags?.includes(EXTERNAL_IMAGE_USAGE_TAGS.FEATURED),
      )
      .sort((a, b) => (a?.external_image_type?.sort ?? 1000000) - (b?.external_image_type?.sort ?? 1000000))
      .flatMap((ei) => ({
        id: ei.id,
        vimeo_embed_id: `https://images.goldenartistcolors.com/${ei.full_filename}`,
        altText: `${product_name} - ${ei?.external_image_type?.name ?? ""}`,
        fullImageUrl: null,
        caption: ei.caption,
      })),
  );

  return product_images;
};

export const GetImageByUseFromExternalImages = (
  external_images: readonly Queries.ExternalImageFragment[],
  imageUse: string,
  altText = "Thumbnail",
): ISimpleImage | null => {
  const images = [...external_images];

  images.sort((a, b) => (a.external_image_type.sort ?? 1000000) - (b.external_image_type.sort ?? 1000000));

  const thumbnail = images.find((i) => i.external_image_type.usage_tags?.includes(imageUse) ?? false);

  return thumbnail
    ? {
        id: "none",
        fullImageUrl: `https://images.goldenartistcolors.com/${thumbnail.full_filename ?? ""}`,
        altText,
        vimeo_embed_id: null,
        caption: null,
      }
    : null;
};

export const GetThumbnailImageFromExternalImages = (
  external_images: readonly Queries.ExternalImageFragment[],
  altText = "Thumbnail",
): ISimpleImage | null => GetImageByUseFromExternalImages(external_images, EXTERNAL_IMAGE_USAGE_TAGS.THUMBNAIL, altText);

export const GetThumbnailImage = (product: Queries.ProductFragment): ISimpleImage | null => {
  const { external_images } = product;
  const images = [...external_images];

  return GetThumbnailImageFromExternalImages(images, `${product?.product_name ?? ""} swatch`);
};
